/** WELDING PROJECTS THAT SELL™ — all customer-facing content and asset mapping (file name kept for the template). */
import { BONUS_COVERS } from './bonusCovers'

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume {
  number: string
  title: string
  projects: string
  level: string
  levelName: string
  description: string
  cover: PlanImage
  projectList: string[]
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'rights'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Bonus {
  number: string
  title: string
  benefit: string
  value: string
  cover: PlanImage
}

export interface Faq {
  question: string
  answer: string
}

export interface SellCard extends PlanImage {
  title: string
  line: string
}

/** Image host: light WebP files in the store's Impultienda resources. */
export const IMAGE_ORIGIN = 'https://aekpgsbqjzqntlbucbts.supabase.co'
const IMG = `${IMAGE_ORIGIN}/storage/v1/object/public/store-resources/6c1a724c-31a4-442a-bc25-af15f66c889e/`

export const PRODUCT = {
  name: 'WELDING PROJECTS THAT SELL™',
  descriptor: '30 welding projects people actually buy—for the home, the garden, pets, bikes and the ranch',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'BUILD & SELL RIGHTS INCLUDED',
  sentence: 'Build them for yourself—or sell every piece you make.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  '30 WELDING PROJECTS THAT SELL',
  'CUT LISTS IN INCHES + MILLIMETERS',
  'STEEL COST & PRICE CARD ON EVERY PROJECT',
  'NO PLASMA OR CNC NEEDED',
  '7-DAY MONEY-BACK GUARANTEE',
  'BUILD & SELL RIGHTS',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Preview', href: '#project-preview' },
  { label: 'The Volumes', href: '#volumes' },
  { label: 'Bonuses', href: '#bonuses' },
  { label: 'Inside the Plans', href: '#technical-proof' },
  { label: 'FAQ', href: '#faq' },
]

const PAGE_SIZE = { width: 1024, height: 1280 }

export const SNAPSHOT: SnapshotItem[] = [
  { label: '30 Projects That Sell', icon: 'projects' },
  { label: '6 Volumes · 240 Pages', icon: 'volumes' },
  { label: 'Cut Lists in in + mm', icon: 'dimensions' },
  { label: 'Build & Sell Rights', icon: 'rights' },
]

export const HERO_BG: PlanImage = { src: `${IMG}1793809210765_hero-bg-0ba476d3.webp`, alt: 'Welded steel firewood rack and backyard builds at dusk', width: 1600, height: 1125 }
export const FINAL_BG: PlanImage = { src: `${IMG}1793809205396_final-bg-06e0857c.webp`, alt: 'Garage man-cave with welded racks, a dog bowl stand and a drink caddy', width: 1600, height: 950 }

/** Best sellers: real builds from the collection, one line each. */
export const SELL_CARDS: SellCard[] = [
  { src: `${IMG}1793813387671_sell-bikerack-dbd3966a.webp`, title: 'Three-Bike Ground Rack', line: 'Every family garage needs one.', alt: 'Black steel three-bike ground rack holding three bicycles in a garage', width: 800, height: 600 },
  { src: `${IMG}1793809287459_sell-plantstand-d9704108.webp`, title: '3-Tier Plant Stand', line: 'Spring and summer best seller.', alt: 'Three-tier square steel plant stand with flower pots', width: 800, height: 600 },
  { src: `${IMG}1793809272060_sell-dogbowl-1a2d21fd.webp`, title: 'Raised Dog Bowl Stand', line: 'Pet owners pay for good-looking feeders.', alt: 'Raised steel dog bowl stand with two stainless bowls', width: 800, height: 600 },
  { src: `${IMG}1793813392090_sell-bootrack-fcbe8e53.webp`, title: 'Two-Tier Boot & Shoe Rack', line: 'Entryways, mudrooms, ranch houses.', alt: 'Two-tier black steel boot and shoe rack full of boots and shoes', width: 800, height: 600 },
  { src: `${IMG}1793813396953_sell-caddy-9451e830.webp`, title: 'Six-Bottle Drink Caddy', line: 'The gift for him that sells all year.', alt: 'Black steel six-bottle drink caddy with beer bottles on a workbench', width: 800, height: 600 },
  { src: `${IMG}1793809281711_sell-lograck-0fcdfaa6.webp`, title: 'Porch Firewood Log Rack', line: 'A steady seller every fall.', alt: 'Square-tube firewood rack full of split logs on a porch', width: 800, height: 600 },
]

export const PROJECT_NAME = 'P11 — 3-Tier Square Plant Stand (Volume 03)'

export const PROJECT_PAGES: ProjectPage[] = [
  { src: `${IMG}1793813402162_p11-1-5f8f3010.webp`, caption: 'Finished Build & Size', alt: 'Volume 03, page 5: 3-tier square plant stand, 14 × 14 × 36 in, finished build', ...PAGE_SIZE },
  { src: `${IMG}1793813408283_p11-2-00645500.webp`, caption: 'Three-View Dimensioned Plan', alt: 'Volume 03, page 6: front, side and top views of the 3-tier plant stand with every dimension in inches and millimeters', ...PAGE_SIZE },
  { src: `${IMG}1793813414355_p11-3-87fca275.webp`, caption: 'Exploded View & Cut List', alt: 'Volume 03, page 7: exploded view and complete cut list of the 3-tier plant stand', ...PAGE_SIZE },
  { src: `${IMG}1793832467366_p11-p8-32abe95d.webp`, caption: 'Step-by-Step Fabrication', alt: 'Volume 03, page 8: illustrated step-by-step fabrication of the 3-tier plant stand', ...PAGE_SIZE },
  { src: `${IMG}1793832472477_p11-p9-dfb84c7c.webp`, caption: 'Weld Map & Details', alt: 'Volume 03, page 9: weld map, tack order and finish checklist of the 3-tier plant stand', ...PAGE_SIZE },
  { src: `${IMG}1793813419945_p11-4-251f71f4.webp`, caption: 'Build & Sell Card', alt: 'Volume 03, page 10: build and sell card for the 3-tier plant stand', ...PAGE_SIZE },
]

export const PLAN_BENEFITS: string[] = ['Pick a project.', 'Cut a batch with a stop block.', 'Price it and sell it.']

export const TECH_PROOF: TechProof[] = [
  { src: `${IMG}1793813429965_proof-bikes-d2fe2a9b.webp`, title: 'For every family garage', project: 'Three-Bike Ground Rack · Vol. 06', description: 'Square tube base and three bike loops.', alt: 'Volume 06: three-bike ground rack three-view plan', ...PAGE_SIZE },
  { src: `${IMG}1793813424996_proof-hook-3a9f60ea.webp`, title: 'Garden favorite', project: 'Double Shepherd’s Hook · Vol. 03', description: 'Ground post, two arms, a foot step.', alt: 'Volume 03: double shepherd’s hook three-view plan', ...PAGE_SIZE },
  { src: `${IMG}1793809260987_proof-3-b0a902b2.webp`, title: 'For pet owners', project: 'Raised Dog Bowl Stand · Vol. 05', description: 'One plate, four legs, two bowls.', alt: 'Volume 05: raised dog bowl stand three-view plan', ...PAGE_SIZE },
  { src: `${IMG}1793809266976_proof-4-5b6ab04f.webp`, title: 'Word-of-mouth seller', project: 'Steel Mailbox Post · Vol. 06', description: 'Post, arm, platform and gusset.', alt: 'Volume 06: steel mailbox post three-view plan', ...PAGE_SIZE },
]

export const COVER_VOLUME_01: PlanImage = { src: `${IMG}1793809172606_cover-v01-039837f3.webp`, alt: 'Volume 01 cover: Welding Projects That Sell™ — Christmas & Gift Builds', width: 900, height: 1125 }

export const HERO_MOCKUP: PlanImage = {
  src: `${IMG}1793809223028_hero-mockup-62e5bdc7.webp`,
  alt: 'WELDING PROJECTS THAT SELL™: the six volume covers and a "+5 free bonuses" seal',
  width: 1200,
  height: 1200,
}

export const BONUS_LINE = 'Plus 5 premium bonus resources, included free.'

const cover = (src: string, number: string, title: string): PlanImage => ({
  src,
  alt: `Volume ${number} cover: Welding Projects That Sell™ — ${title}`,
  width: 900,
  height: 1125,
})

export const VOLUMES: Volume[] = [
  {
    number: '01', title: 'Christmas & Gift Builds', projects: '5 PROJECTS', level: 'GIFTS', levelName: 'Holiday season',
    description: 'Horseshoe trees, rebar trees, hearts, barn stars and spike racks.',
    projectList: ['Horseshoe Christmas Tree', 'Rebar Yard Christmas Tree', 'Horseshoe Heart Wall Decor', 'Flat Bar Star Wall Decor', 'Railroad Spike Coat Rack'],
    cover: COVER_VOLUME_01,
  },
  {
    number: '02', title: 'Fire Pit & Fireplace Builds', projects: '5 PROJECTS', level: 'BACKYARD', levelName: 'Spring & fall',
    description: 'Fire pit, spark screen, campfire grill, log rack and tool stand.',
    projectList: ['Square Steel Fire Pit', 'Fireplace Tool Stand & Poker', 'Porch Firewood Log Rack', 'Swing-Arm Campfire Grill', 'Fire Pit Spark Screen'],
    cover: cover(`${IMG}1793809177691_cover-v02-84d4fc90.webp`, '02', 'Fire Pit & Fireplace Builds'),
  },
  {
    number: '03', title: 'Garden & Yard Decor', projects: '5 PROJECTS', level: 'GARDEN', levelName: 'Every spring',
    description: 'Plant stand, obelisk, shepherd’s hook, boot scraper and hose holder.',
    projectList: ['3-Tier Square Plant Stand', 'Rebar Garden Obelisk Trellis', 'Double Shepherd’s Hook', 'Boot Scraper with Brushes', 'Wall-Mount Hose Holder'],
    cover: cover(`${IMG}1793809183355_cover-v03-288b43f3.webp`, '03', 'Garden & Yard Decor'),
  },
  {
    number: '04', title: 'Home & Kitchen Decor', projects: '5 PROJECTS', level: 'HOME', levelName: 'All year',
    description: 'Shelf brackets, pot rack, candle holders, bottle opener and record rack.',
    projectList: ['Industrial Shelf Brackets (Pair)', 'Hanging Pot Rack', 'Industrial Candle Holder Set (3)', 'Wall Bottle Opener with Cap Catcher', 'Vinyl Record Rack'],
    cover: cover(`${IMG}1793809188154_cover-v04-88c7efdc.webp`, '04', 'Home & Kitchen Decor'),
  },
  {
    number: '05', title: 'Man-Cave, Pet & Entryway', projects: '5 PROJECTS', level: 'GIFTS FOR HIM', levelName: 'Father’s Day & birthdays',
    description: 'Rod rack, dog bowl stand, boot rack, bookends and drink caddy.',
    projectList: ['Wall Fishing Rod Rack', 'Raised Dog Bowl Stand', 'Two-Tier Boot & Shoe Rack', 'Steel Bookends (Pair)', 'Six-Bottle Drink Caddy'],
    cover: cover(`${IMG}1793809193694_cover-v05-b1057780.webp`, '05', 'Man-Cave, Pet & Entryway Builds'),
  },
  {
    number: '06', title: 'Ranch, Mailbox & Sign Builds', projects: '5 PROJECTS', level: 'RANCH', levelName: 'Word of mouth',
    description: 'Mailbox post, saddle rack, sign bracket, bike rack and flag holder.',
    projectList: ['Steel Mailbox Post', 'Wall Saddle Rack', 'Porch Sign Bracket', 'Three-Bike Ground Rack', 'Wall Flag Holder'],
    cover: cover(`${IMG}1793809199512_cover-v06-641f6bc5.webp`, '06', 'Ranch, Mailbox & Sign Builds'),
  },
]

const BONUS_COVER_SIZE = { width: 800, height: 800 }

const bonus = (number: string, title: string, benefit: string, value: string, src: string): Bonus => ({
  number, title, benefit, value,
  cover: { src: BONUS_COVERS[src] ?? src, alt: `Bonus ${number}: ${title} — Welding Projects That Sell™`, ...BONUS_COVER_SIZE },
})

export const BONUSES: Bonus[] = [
  bonus('01', 'Steel Pricing & Profit Calculator', 'Price every piece from real costs.', 'US$19', 'BONUS_01'),
  bonus('02', 'Where & How to Sell Your Welding Projects', 'Marketplace, fairs, Etsy and neighbors.', 'US$19', 'BONUS_02'),
  bonus('03', 'Batch Cutting & Holiday Stock Planner', 'Build a full table in batches.', 'US$15', 'BONUS_03'),
  bonus('04', 'Rust, Paint & Clear-Coat Finishing Guide', 'The finish that sells the piece.', 'US$12', 'BONUS_04'),
  bonus('05', 'Printable Quote, Receipt & Custom Order Forms', 'Take custom orders like a pro.', 'US$12', 'BONUS_05'),
]

export const BONUS_TOTAL = 'US$77'

export const OFFER_FEATURES: string[] = [
  '30 welding projects that sell in 6 premium PDF volumes (240 pages)',
  'Bike racks, plant stands, dog stands, boot racks, home decor and ranch builds',
  '3-view plans + cut lists in inches and millimeters',
  'Step-by-step fabrication and a weld map for every project',
]

export const SUPPORT_EMAIL = 'soporte@creativodigital.shop'

/** Premium answers first, then the objections a welder has before buying. */
export const FAQS: Faq[] = [
  { question: 'What makes this a premium collection?', answer: 'Other packs sell thousands of random PDFs. This collection is 30 complete projects, each with a finished photo, 3-view dimensioned plan, cut list in inches and millimeters, step-by-step fabrication, a weld map and a build & sell card. Free updates, lifetime access and personal email support are included.' },
  { question: 'Do I get future updates?', answer: 'Yes, free. When a new edition is released, it is added to the same download link you receive after purchase. You never pay for an update.' },
  { question: 'What if I get stuck on a build?', answer: `Write to ${SUPPORT_EMAIL}. A real person reads every email and answers questions about your project.` },
  { question: 'I’m a hobby welder. Can I build these?', answer: 'Yes. Every project is built from standard steel stock. Start with the plant stand or the dog bowl stand—each takes an afternoon.' },
  { question: 'What tools do I need?', answer: 'A chop saw or metal band saw, an angle grinder, a drill and a MIG or stick welder. No plasma cutter or CNC needed.' },
  { question: 'Are these physical items or printed books?', answer: 'Neither. You get digital PDF plans—6 volumes and 5 bonuses—by email.' },
  { question: 'Can I preview the plans?', answer: 'Yes. Scroll up to see every page of the 3-Tier Square Plant Stand project, plus plan pages from four more projects.' },
  { question: 'Can I sell what I build?', answer: 'Yes. Sell every piece at markets, on Marketplace, on Etsy or to your neighbors. The digital plans may not be resold or shared.' },
  { question: 'Does it tell me what to charge?', answer: 'Every project has a build & sell card to fill in with your steel cost, hours and price, plus a pricing calculator bonus. Your local market sets the final price.' },
  { question: 'Is this a monthly membership?', answer: 'No. One payment, and the 30 projects and 5 bonuses are yours forever.' },
  { question: 'What if it is not for me?', answer: `7-day money-back guarantee. Email ${SUPPORT_EMAIL} and we refund 100%.` },
]

export const GUARANTEE_LINE = '7-day money-back guarantee · 100% refund, no questions asked.'

export const AUDIENCE: string[] = ['Garage & hobby welders', 'Marketplace makers', 'Farm & ranch welders', 'Side-income welders']

export const NUMBERS: Array<{ value: string; label: string }> = [
  { value: '6', label: 'volumes' },
  { value: '30', label: 'projects' },
  { value: '240', label: 'pages' },
  { value: '5', label: 'bonuses' },
]

export const PAINS: string[] = []

export const COSTS: Array<{ title: string; text: string }> = []

export const WHY_TODAY: string[] = [
  'Holiday season: markets and gift orders are happening now.',
  'Launch price: 64% off the 2026 Edition, for a limited time.',
  'Start this weekend: the plant stand and the dog bowl stand take an afternoon.',
]
